import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2, Film, AlertCircle, CheckCircle2, Clock, Captions, RefreshCw } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Render } from "@shared/schema";

// Which pipeline's render endpoints this control talks to. UGC (the default)
// uses /api/ads/* + /api/renders/*; VideoExpress uses the parallel
// /api/video-express-scripts/* + /api/video-express-renders/* routes. A VE
// script id sent to the UGC route 404s ("Ad (script) not found"), surfacing as
// "Render failed to start" — so the host page must pass the matching variant.
type RenderVariant = "ugc" | "video-express";

function renderEndpoints(variant: RenderVariant, scriptId: string) {
  if (variant === "video-express") {
    return {
      post: `/api/video-express-scripts/${scriptId}/render`,
      latest: `/api/video-express-scripts/${scriptId}/render/latest`,
      poll: (id: string) => `/api/video-express-renders/${id}`,
      captions: (id: string) => `/api/video-express-renders/${id}/generate-captions`,
      history: `/api/video-express-scripts/${scriptId}/renders`,
    };
  }
  return {
    post: `/api/ads/${scriptId}/render`,
    latest: `/api/ads/${scriptId}/render/latest`,
    poll: (id: string) => `/api/renders/${id}`,
    captions: (id: string) => `/api/renders/${id}/generate-captions`,
    history: null as string | null,
  };
}

interface Props {
  scriptId: string;
  hasScenes: boolean;
  variant?: RenderVariant;
}

export function ScriptRenderControls({ scriptId, hasScenes, variant = "ugc" }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [activeRenderId, setActiveRenderId] = useState<string | null>(null);
  const ep = renderEndpoints(variant, scriptId);

  // Load latest render for this script (initial state)
  const { data: latest } = useQuery<Render | null>({
    queryKey: ["render-latest", variant, scriptId],
    queryFn: async () => {
      const res = await fetch(ep.latest, { credentials: "include" });
      if (res.status === 204) return null;
      if (!res.ok) throw new Error("Failed to load render status");
      return res.json();
    },
  });

  useEffect(() => {
    if (latest && (latest.status === "queued" || latest.status === "rendering")) {
      setActiveRenderId(latest.id);
    }
  }, [latest]);

  // Poll the active render
  const { data: render } = useQuery<Render>({
    queryKey: ["render-poll", variant, activeRenderId],
    queryFn: async () => {
      const res = await fetch(ep.poll(activeRenderId as string), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch render");
      return res.json();
    },
    enabled: !!activeRenderId,
    refetchInterval: (q) => {
      const d = q.state.data as Render | undefined;
      if (!d) return 2000;
      if (d.status === "complete" || d.status === "failed") return false;
      return 2000;
    },
  });

  useEffect(() => {
    if (render && (render.status === "complete" || render.status === "failed")) {
      qc.invalidateQueries({ queryKey: ["render-latest", variant, scriptId] });
      qc.invalidateQueries({ queryKey: ["render-history", variant, scriptId] });
    }
  }, [render?.status, qc, scriptId, render, variant]);

  // Full render history for this script (Video Express only). The newest
  // render is shown as "current" above; older ones list as past generations.
  const { data: history } = useQuery<HistoryRender[]>({
    queryKey: ["render-history", variant, scriptId],
    queryFn: async () => {
      const res = await fetch(ep.history as string, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load render history");
      return res.json();
    },
    enabled: !!ep.history,
  });

  const current = render || latest;

  const renderMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", ep.post);
      return res.json() as Promise<Render>;
    },
    onSuccess: (r) => {
      setActiveRenderId(r.id);
      qc.invalidateQueries({ queryKey: ["render-history", variant, scriptId] });
      toast({ title: "Render queued", description: "Stitching scenes and rendering final video..." });
    },
    onError: (e: any) => {
      toast({ title: "Render failed to start", description: e.message, variant: "destructive" });
    },
  });

  const isBusy = renderMutation.isPending || (current && (current.status === "queued" || current.status === "rendering"));
  const status = current?.status;

  return (
    <div className="rounded-xl border border-border/40 bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Film className="h-3.5 w-3.5 text-violet-500 shrink-0" />
          <span className="text-xs font-semibold">Final stitched video</span>
          {status === "queued" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600" data-testid={`render-status-${scriptId}`}>
              <Clock className="h-3 w-3" /> Queued
            </span>
          )}
          {status === "rendering" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-600" data-testid={`render-status-${scriptId}`}>
              <Loader2 className="h-3 w-3 animate-spin" /> Rendering {current?.progress ?? 0}%
            </span>
          )}
          {status === "complete" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600" data-testid={`render-status-${scriptId}`}>
              <CheckCircle2 className="h-3 w-3" /> Complete
            </span>
          )}
          {status === "failed" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-red-500/10 text-red-600" data-testid={`render-status-${scriptId}`}>
              <AlertCircle className="h-3 w-3" /> Failed
            </span>
          )}
        </div>
        <Button
          size="sm"
          className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
          disabled={!hasScenes || !!isBusy}
          onClick={() => renderMutation.mutate()}
          data-testid={`button-render-final-${scriptId}`}
        >
          {isBusy ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Rendering…</>
          ) : status === "complete" ? (
            <><Film className="h-3.5 w-3.5 mr-1.5" /> Re-render</>
          ) : (
            <><Film className="h-3.5 w-3.5 mr-1.5" /> Render Final Video</>
          )}
        </Button>
      </div>

      {!hasScenes && (
        <p className="text-[11px] text-muted-foreground">Every scene clip must be ready before the final video can be rendered.</p>
      )}

      {status === "failed" && current?.error && (
        <p className="text-[11px] text-red-600 break-words" data-testid={`render-error-${scriptId}`}>{current.error}</p>
      )}

      {status === "complete" && current?.outputUrl && (
        <CaptionedPanel scriptId={scriptId} variant={variant} render={current as any} />
      )}

      <PastGenerations scriptId={scriptId} history={history} currentId={current?.id} />
    </div>
  );
}

type HistoryRender = Render & {
  captionedVideoUrl?: string | null;
  captionedVideoStatus?: string | null;
  captionedVideoError?: string | null;
};

function formatRenderDate(d: string | Date | null | undefined): string {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Past final renders for this script (newest first), excluding the render
// shown as "current" above. Read-only history: no delete / re-run here.
function PastGenerations({
  scriptId,
  history,
  currentId,
}: {
  scriptId: string;
  history?: HistoryRender[];
  currentId?: string;
}) {
  const past = (history || []).filter((r) => r.id !== currentId);
  if (past.length === 0) return null;
  return (
    <div className="pt-2 border-t border-border/30 space-y-2" data-testid={`past-generations-${scriptId}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
        <Clock className="h-3 w-3" /> Past generations ({past.length})
      </p>
      <div className="space-y-2">
        {past.map((r) => {
          const captioned = r.captionedVideoStatus === "ready" && r.captionedVideoUrl;
          return (
            <details key={r.id} className="rounded-lg border border-border/40 bg-muted/10 overflow-hidden" data-testid={`past-render-${r.id}`}>
              <summary className="flex items-center gap-2 px-2.5 py-2 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden hover:bg-muted/30 transition-colors">
                <Film className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="text-[11px] text-muted-foreground flex-1 min-w-0 truncate" data-testid={`past-render-date-${r.id}`}>
                  {formatRenderDate(r.createdAt)}
                </span>
                {captioned ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-fuchsia-600 shrink-0">
                    <Captions className="h-2.5 w-2.5" /> Captioned
                  </span>
                ) : null}
                {r.status === "complete" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0" data-testid={`past-render-status-${r.id}`}>
                    <CheckCircle2 className="h-3 w-3" /> Complete
                  </span>
                )}
                {r.status === "failed" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 shrink-0" data-testid={`past-render-status-${r.id}`}>
                    <AlertCircle className="h-3 w-3" /> Failed
                  </span>
                )}
                {(r.status === "queued" || r.status === "rendering") && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 shrink-0" data-testid={`past-render-status-${r.id}`}>
                    <Loader2 className="h-3 w-3 animate-spin" /> {r.status === "queued" ? "Queued" : "Rendering"}
                  </span>
                )}
              </summary>
              <div className="px-2.5 pb-2.5 pt-1 space-y-2 border-t border-border/30">
                {r.status === "failed" && (
                  <p className="text-[11px] text-red-600 break-words" data-testid={`past-render-error-${r.id}`}>
                    {r.error || "Render failed"}
                  </p>
                )}
                {captioned && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Captioned</p>
                    <video
                      src={r.captionedVideoUrl as string}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded-lg border border-border/40 bg-black"
                      data-testid={`past-video-captioned-${r.id}`}
                    />
                    <a
                      href={r.captionedVideoUrl as string}
                      download
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-fuchsia-600 hover:text-fuchsia-700"
                      data-testid={`past-link-captioned-${r.id}`}
                    >
                      Download captioned MP4
                    </a>
                  </div>
                )}
                {r.outputUrl && (
                  <div className="space-y-1">
                    {captioned ? (
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Original (no captions)</p>
                    ) : null}
                    <video
                      src={r.outputUrl}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded-lg border border-border/40 bg-black"
                      data-testid={`past-video-${r.id}`}
                    />
                    <a
                      href={r.outputUrl}
                      download
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-violet-600 hover:text-violet-700"
                      data-testid={`past-link-download-${r.id}`}
                    >
                      Download MP4
                    </a>
                  </div>
                )}
                {!r.outputUrl && r.status !== "failed" && (
                  <p className="text-[11px] text-muted-foreground">No video output for this render.</p>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}

interface CaptionedPanelProps {
  scriptId: string;
  variant: RenderVariant;
  render: Render & {
    captionedVideoUrl?: string | null;
    captionedVideoStatus?: string | null;
    captionedVideoError?: string | null;
  };
}

function CaptionedPanel({ scriptId, variant, render }: CaptionedPanelProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const ep = renderEndpoints(variant, scriptId);

  const propStatus = render.captionedVideoStatus || "idle";

  // Poll the render row while captioning is in flight, and prefer polled
  // data over the prop so status transitions are live and deterministic.
  const { data: polled } = useQuery<Render & {
    captionedVideoUrl?: string | null;
    captionedVideoStatus?: string | null;
    captionedVideoError?: string | null;
  }>({
    queryKey: ["render-caption-poll", variant, render.id],
    queryFn: async () => {
      const res = await fetch(ep.poll(render.id), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to poll captioning");
      return res.json();
    },
    // Captioning auto-starts server-side the moment the stitch completes, so
    // keep polling through the brief "idle" window before the job claims the
    // slot — only stop once it reaches a terminal (ready/failed) state.
    enabled: propStatus === "idle" || propStatus === "generating" || submitting,
    refetchInterval: 2500,
    refetchOnWindowFocus: false,
  });

  const effective = polled ?? render;
  const status = effective.captionedVideoStatus || "idle";
  const url = effective.captionedVideoUrl;
  const error = effective.captionedVideoError;
  // "idle" here means the auto-caption job hasn't flipped the row to
  // "generating" yet — still a waiting state, never show the raw cut.
  const busy = submitting || status === "generating" || status === "idle";

  const captionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", ep.captions(render.id));
      return res.json();
    },
    onMutate: () => setSubmitting(true),
    onSuccess: () => {
      toast({ title: "Captioning queued", description: "fal-ai/veed/subtitles is burning captions in (preset: fusion). Usually 30–90 s." });
      qc.invalidateQueries({ queryKey: ["render-caption-poll", variant, render.id] });
      qc.invalidateQueries({ queryKey: ["render-latest", variant, scriptId] });
    },
    onError: (e: any) => {
      toast({ title: "Captioning failed to start", description: e.message, variant: "destructive" });
    },
    onSettled: () => setSubmitting(false),
  });

  // Reflect terminal state into the render-latest cache so list views update.
  useEffect(() => {
    if (status === "ready" || status === "failed") {
      qc.invalidateQueries({ queryKey: ["render-latest", variant, scriptId] });
    }
  }, [status, qc, scriptId, variant]);

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 p-2.5 space-y-2" data-testid={`panel-captions-${scriptId}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Captions className="h-3.5 w-3.5 text-fuchsia-500 shrink-0" />
          <span className="text-[11px] font-semibold">Captioned video</span>
          {busy && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-fuchsia-600">
              <Loader2 className="h-2.5 w-2.5 animate-spin" /> Adding captions…
            </span>
          )}
          {!busy && status === "ready" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
              <CheckCircle2 className="h-2.5 w-2.5" /> Ready
            </span>
          )}
          {!busy && status === "failed" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600">
              <AlertCircle className="h-2.5 w-2.5" /> Failed
            </span>
          )}
        </div>
        {!busy && (status === "ready" || status === "failed") && (
          <Button
            size="sm"
            variant={status === "ready" ? "outline" : "default"}
            className={`h-7 text-[11px] rounded-full ${status === "failed" ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white" : ""}`}
            onClick={() => captionMutation.mutate()}
            data-testid={`button-generate-captions-${scriptId}`}
          >
            {status === "ready" ? <><RefreshCw className="h-3 w-3 mr-1" /> Re-caption</>
              : <><RefreshCw className="h-3 w-3 mr-1" /> Retry</>}
          </Button>
        )}
      </div>
      {busy && (
        <p className="text-[11px] text-muted-foreground" data-testid={`text-captions-waiting-${scriptId}`}>
          Burning captions into the final video. This usually takes 30–90 seconds.
        </p>
      )}
      {!busy && status === "failed" && error && (
        <p className="text-[11px] text-red-600 break-words" data-testid={`text-captions-error-${scriptId}`}>{error}</p>
      )}
      {/* Captioning (fal veed) is a flaky post-step. When it fails the stitched
          render itself still succeeded, so always surface the completed
          uncaptioned final rather than leaving the user with a bare "Failed". */}
      {!busy && status === "failed" && effective.outputUrl && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-muted-foreground" data-testid={`text-captions-fallback-${scriptId}`}>
            Captions couldn't be added, but your final video is ready.
          </p>
          <video
            src={effective.outputUrl}
            controls
            playsInline
            className="w-full rounded-lg border border-border/40 bg-black"
            data-testid={`video-uncaptioned-${scriptId}`}
          />
          <a
            href={effective.outputUrl}
            download
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-fuchsia-600 hover:text-fuchsia-700"
            data-testid={`link-download-uncaptioned-${scriptId}`}
          >
            Download final MP4
          </a>
        </div>
      )}
      {!busy && status === "ready" && url && (
        <div className="space-y-1.5">
          <video
            src={url}
            controls
            playsInline
            className="w-full rounded-lg border border-border/40 bg-black"
            data-testid={`video-captioned-${scriptId}`}
          />
          <a
            href={url}
            download
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-fuchsia-600 hover:text-fuchsia-700"
            data-testid={`link-download-captioned-${scriptId}`}
          >
            Download captioned MP4
          </a>
        </div>
      )}
    </div>
  );
}
